import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, MapPin, Radius, Rocket, BarChart3, Radio, Target, Globe2, MousePointerClick } from "lucide-react";
import { Button } from "@/components/ui/button";

const HERO_IMG = "https://images.unsplash.com/photo-1550030272-2695f03d3088?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxhZXJpYWwlMjBjaXR5JTIwbmlnaHQlMjBuZXR3b3JrJTIwY29ubmVjdGlvbnMlMjBkYXJrJTIwYmx1ZSUyMHRlY2hub2xvZ3l8ZW58MHx8fGJsdWV8MTc4ODgyMTA5Mnww&ixlib=rb-4.1.0&q=85";

const MORPH_WORDS = ["GEOFENCING", "GEOLOCATION", "LOCATION INTELLIGENCE", "PRECISION TARGETING"];

function MorphWord() {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIndex((v) => (v + 1) % MORPH_WORDS.length), 2600);
    return () => clearInterval(id);
  }, []);
  return (
    <span className="block h-[1.15em] overflow-hidden" data-testid="morph-word">
      <AnimatePresence mode="wait">
        <motion.span
          key={index}
          initial={{ opacity: 0, filter: "blur(14px)", letterSpacing: "0.25em", y: 18 }}
          animate={{ opacity: 1, filter: "blur(0px)", letterSpacing: "0.01em", y: 0 }}
          exit={{ opacity: 0, filter: "blur(14px)", letterSpacing: "-0.04em", y: -18 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="inline-block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-cyan-300 to-blue-400"
        >
          {MORPH_WORDS[index]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

const STEPS = [
  { icon: MapPin, num: "01", title: "Select Your Location", text: "Search for any address, venue, stadium, mall, transport hub, business or geographic location." },
  { icon: Radius, num: "02", title: "Create Your Geofence", text: "Select a radius around the location — 100m, 250m, 500m, 750m or 1km — or set a custom radius." },
  { icon: Rocket, num: "03", title: "Launch Your Campaign", text: "Upload MP4 video, JPG, PNG or GIF creatives, enter campaign details and set your duration." },
  { icon: BarChart3, num: "04", title: "Measure Everything", text: "Track impressions, reach, engagement, clicks, video completion and geographic performance in real time." },
];

const SERVICES_PREVIEW = [
  { icon: Target, title: "Precision Geofencing", text: "Location-based advertising zones around physical locations." },
  { icon: Radio, title: "Live Campaign Analytics", text: "Monitor campaign activity as it happens, in real time." },
  { icon: Globe2, title: "Interactive Campaign Mapping", text: "Visualise campaigns and geofences on interactive maps." },
  { icon: MousePointerClick, title: "Geo-Targeted Advertising", text: "Reach audiences based on defined geographic areas." },
];

function AnimatedStat({ label, value, suffix = "", ticking = false }) {
  const [display, setDisplay] = useState(0);
  const [target, setTarget] = useState(value);
  useEffect(() => {
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min((t - start) / 1800, 1);
      setDisplay(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  useEffect(() => {
    if (!ticking) return;
    const id = setInterval(() => setTarget((v) => v + Math.floor(Math.random() * 37) + 3), 2400);
    return () => clearInterval(id);
  }, [ticking]);
  return (
    <div data-testid={`hero-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <p className="font-display text-2xl sm:text-3xl font-bold text-white">{display.toLocaleString("en-ZA")}{suffix}</p>
      <p className="text-[10px] sm:text-[11px] tracking-[0.2em] text-cyan-400/80 mt-1">{label}</p>
    </div>
  );
}

export default function Home() {
  return (
    <div>
      <section className="relative overflow-hidden min-h-[92vh] flex items-center">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" data-testid="hero-image" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#04070d] via-[#04070d]/85 to-[#04070d]/25" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32 w-full">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
            <p className="section-label mb-5">Location Intelligence · Geofencing · Digital Advertising</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.06] tracking-tight text-white">
              THE WORLD'S FIRST INTERACTIVE
              <MorphWord />
              ADVERTISING PLATFORM
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl">
              Reach the right audience in the right place at the right time. GEOTRAQ AI transforms physical
              locations into intelligent digital advertising opportunities using precision geofencing,
              geolocation intelligence and real-time campaign analytics.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/contact" data-testid="hero-launch-btn">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-500 text-white font-semibold px-7 shadow-[0_0_30px_rgba(37,99,235,0.35)]">
                  Launch a Campaign <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/how-it-works" data-testid="hero-explore-btn">
                <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10 px-7 bg-white/5 backdrop-blur-sm">
                  Explore GEOTRAQ AI
                </Button>
              </Link>
            </div>
            <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 border border-white/10 rounded-md overflow-hidden max-w-2xl" data-testid="hero-stats">
              {[
                ["LIVE CAMPAIGNS", 128, false, ""],
                ["ACTIVE GEOFENCES", 342, false, ""],
                ["AUDIENCE ENGAGEMENTS", 1284502, true, ""],
                ["LOCATIONS REACHED", 56, false, ""],
              ].map(([label, value, ticking, suffix]) => (
                <div key={label} className="bg-[#04070d]/70 backdrop-blur-md px-4 py-5">
                  <AnimatedStat label={label} value={value} ticking={ticking} suffix={suffix} />
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="border-y border-white/5 bg-card/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-xs font-semibold tracking-[0.18em] text-slate-500 uppercase">
          {["Banking", "Telecoms", "Retail", "Betting & Gaming", "Automotive", "Healthcare", "Tourism", "FMCG", "Government", "Media"].map((i) => (
            <span key={i} className="hover:text-cyan-400 transition-colors">{i}</span>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <p className="section-label">How It Works</p>
        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-white tracking-tight">TURN LOCATIONS INTO OPPORTUNITIES</h2>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {STEPS.map((s, i) => (
            <motion.div key={s.num} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="card-surface p-6 hover:border-cyan-500/30 transition-colors group" data-testid={`step-${s.num}`}>
              <div className="flex items-center justify-between">
                <s.icon className="w-6 h-6 text-cyan-400" />
                <span className="font-display text-4xl font-bold text-white/10 group-hover:text-cyan-400/20 transition-colors">{s.num}</span>
              </div>
              <h3 className="mt-5 font-display font-semibold text-white">{s.title}</h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="grid lg:grid-cols-2 gap-5">
          <div className="card-surface p-8 lg:p-10 flex flex-col justify-between min-h-[320px] bg-gradient-to-br from-card to-blue-950/40">
            <div>
              <p className="section-label">Platform</p>
              <h3 className="mt-3 font-display text-2xl sm:text-3xl font-bold text-white tracking-tight">One platform. Every location. Every impression measured.</h3>
              <p className="mt-4 text-slate-400 leading-relaxed">
                Create intelligent geographic advertising campaigns, activate precise locations and measure
                campaign performance through one powerful location intelligence platform.
              </p>
            </div>
            <div className="mt-8"><Link to="/services" className="text-cyan-400 font-semibold text-sm inline-flex items-center hover:gap-2.5 gap-1.5 transition-all" data-testid="explore-services-link">Explore all services <ArrowRight className="w-4 h-4" /></Link></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {SERVICES_PREVIEW.map((s) => (
              <div key={s.title} className="card-surface p-6 hover:border-blue-500/30 transition-colors">
                <s.icon className="w-6 h-6 text-blue-400" />
                <h4 className="mt-4 font-display font-semibold text-white text-sm">{s.title}</h4>
                <p className="mt-1.5 text-sm text-slate-400">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/5 bg-gradient-to-b from-blue-950/30 to-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
          <h2 className="font-display text-3xl sm:text-5xl font-bold text-white tracking-tight">PUT YOUR BRAND WHERE YOUR CUSTOMERS ARE.</h2>
          <p className="mt-5 text-slate-400 max-w-2xl mx-auto">
            Start your geofencing campaign today and measure real-world impact with location intelligence.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-4">
            <Link to="/contact" data-testid="cta-start-campaign"><Button size="lg" className="bg-blue-600 hover:bg-blue-500 font-semibold px-8">START YOUR CAMPAIGN</Button></Link>
            <Link to="/contact" data-testid="cta-request-demo"><Button size="lg" variant="outline" className="border-white/15 text-white hover:bg-white/5 px-8">REQUEST A DEMO</Button></Link>
            <Link to="/login" data-testid="cta-client-login"><Button size="lg" variant="ghost" className="text-cyan-400 hover:text-cyan-300 hover:bg-white/5 px-8">CLIENT LOGIN</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
